import React, { useState } from 'react';

// Utils
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_YOUTUBE } from '../../util/types';
import {
  isFieldForCategory,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';
import IconCollection from '../../components/IconCollection/IconCollection.js';
import css from './CustomListingFields.module.css';
import { FormattedMessage } from 'react-intl';
import { PrimaryButton } from '../../components/index.js';
import { getLabelFromValue } from '../../util/data.js';

const SectionGeneralAmenities = props => {
  const { heading, options, selectedOptions = [] } = props;
  const [showAll, setShowAll] = useState(false);
  const visibleOptions = showAll ? selectedOptions : selectedOptions.slice(0, 8);
  if (!selectedOptions || selectedOptions.length === 0) {
    return null;
  }

  return (
    <div className={css.sectionMultiEnum}>
      <h4 className={css.amenitiesHeading}>{heading}</h4>
      <div className={css.generalAmenities}>
        {visibleOptions.map(value => (
          <div className={css.amenitie} key={value}>
            <div className={css.iconContainer}>
              <IconCollection iconName={value} />
            </div>
            <span className={css.label}>{getLabelFromValue(options, value)}</span>
          </div>
        ))}

        {!showAll && selectedOptions.length > 8 && (
          <PrimaryButton className={css.showAllBtn} onClick={() => setShowAll(true)}>
            <FormattedMessage id="ListingPage.showAllAmenities" />
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

const SectionSuppliesIncluded = props => {
  const { heading, options, selectedOptions = [] } = props;
  if (!selectedOptions || selectedOptions.length === 0) {
    return null;
  }
  return (
    <div className={`${css.sectionMultiEnum} ${css.suppliesIncludedContainer}`}>
      <h4 className={css.amenitiesHeading}>{heading}</h4>
      <div className={css.suppliesIncluded}>
        {selectedOptions.map(value => (
          <div key={value} className={css.suppliesItem}>
            <div className={css.iconContainer}>
              <IconCollection iconName={value} />
            </div>

            <span className={css.label}>{getLabelFromValue(options, value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionParkingAndAccessibility = props => {
  const { heading, options, selectedOptions = [] } = props;
  if (!selectedOptions || selectedOptions.length === 0) {
    return null;
  }
  return (
    <div className={`${css.sectionMultiEnum} ${css.parkingAccessibilityItemContianer}`}>
      <h4 className={css.amenitiesHeading}>{heading}</h4>
      <div className={css.parkingAccessibilityList}>
        {selectedOptions.map(value => (
          <div key={value} className={css.parkingAccessibilityItem}>
            <div className={css.iconContainer}>
              <IconCollection iconName="circleCheck" />
            </div>

            <div className={css.iconAndLabel}>
              <p className={css.label}>{getLabelFromValue(options, value)}</p>
              <p className={css.description}>
                <FormattedMessage id={'ParkingAndAccessibility.' + value} />
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionEquipmentAndUtilities = props => {
  const { heading, options, selectedOptions = [] } = props;
  if (!selectedOptions || selectedOptions.length === 0) {
    return null;
  }
  return (
    <div className={`${css.sectionMultiEnum} ${css.equipmentUtilitiesContainer}`}>
      <h4 className={css.amenitiesHeading}>{heading}</h4>

      <div className={css.equipmentUtilities}>
        {selectedOptions.map(value => (
          <div key={value}>
            <p>{getLabelFromValue(options, value)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};


/**
 * Renders custom listing fields.
 * - SectionDetailsMaybe is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnumMaybe is used if schemaType is 'multi-enum'
 * - SectionTextMaybe is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      publicData,
      metadata,
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  return (
    <>
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        if (key === 'general_amenities') {
          return <SectionGeneralAmenities key={key} {...fieldProps} />;
        } else if (key === 'supplies_included') {
          return <SectionSuppliesIncluded key={key} {...fieldProps} />;
        } else if (key === 'parking_and_accessibility') {
          return <SectionParkingAndAccessibility key={key} {...fieldProps} />;
        } else if (key === 'equipment_and_utilities') {
          return <SectionEquipmentAndUtilities key={key} {...fieldProps} />;
        }

        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
        ) : null;
      })}

      <SectionDetailsMaybe
        {...props}
        isFieldForCategory={isFieldForSelectedCategories}
      />
    </>
  );
};

export default CustomListingFields;
